import { apiUrl } from "../utils/api";
import { apiFetch } from "../utils/apiFetch";

/**
 * Service des Bereichs „Kontaktdaten bereinigen" (E-Mail-Listen A/B/C) —
 * Endpoints aus apps/api/src/kontaktdaten-cleanup/ (SP3):
 *   A = Zentrale-Mails am AP → zur Firma, B = personenhafte Mails an der
 *   Firma → zum AP, C = Platzhalter-Müll → löschen. „Offen" kommt live aus
 *   der Heuristik, „Erledigt" aus dem Journal (Liste C existiert nach dem
 *   Löschen nur noch dort).
 */

export type CleanupListe = "a" | "b" | "c";
export type CleanupStatus = "open" | "erledigt";

export interface FirmaKandidat {
  firmaId: number;
  name: string | null;
  hatMail: boolean;
  domainMatch: boolean;
}

export interface ApKandidat {
  personId: number;
  name: string | null;
  nameMatch: boolean;
}

/** Eine offene Zeile der Worklist (Basis + listen-spezifische Felder). */
export interface KontaktdatenCleanupRow {
  commId: number;
  value: string;
  label: string | null;
  kind: string | null;
  isDefault: boolean;
  kontaktId: number;
  kontaktName: string | null;
  kontaktTyp: string | null;
  kundennummer: string | null;
  statusLabel: string | null;
  verantwortlich: string | null;
  /** Liste A */
  kandidatenFirmen?: FirmaKandidat[];
  zielFirmaId?: number | null;
  begruendung?: "domain-match" | "einzige-firma" | "mehrere-firmen";
  /** Liste B */
  kandidatenAps?: ApKandidat[];
  zielPersonId?: number | null;
  freemail?: boolean;
  /** Liste C */
  grund?: string;
}

/** Eine Zeile des Erledigt-Tabs (aus dem Journal — die Comm-Zeile kann weg sein). */
export interface KontaktdatenErledigtRow {
  journalId: number;
  commId: number | null;
  aktion: string;
  value: string | null;
  label: string | null;
  sourceContactId: number | null;
  sourceName: string | null;
  targetContactId: number | null;
  targetName: string | null;
  reviewedAt: string | null;
  reviewedBy: string | null;
}

export type KontaktdatenCounts = Record<CleanupListe, { open: number; erledigt: number }>;

export const listKontaktdatenCleanup = async (
  liste: CleanupListe,
  status: CleanupStatus,
  q: string | undefined,
  teamId: string | undefined,
  limit: number,
  offset: number,
  signal?: AbortSignal,
): Promise<{ rows: Array<KontaktdatenCleanupRow | KontaktdatenErledigtRow>; total: number }> => {
  const { data, total } = await apiFetch<Array<KontaktdatenCleanupRow | KontaktdatenErledigtRow>>(
    "kontaktdaten-cleanup",
    {
      params: {
        liste,
        status,
        ...(q ? { q } : {}),
        ...(teamId ? { teamId } : {}),
        limit,
        offset,
      },
      signal,
    },
  );
  return { rows: data ?? [], total: total ?? data?.length ?? 0 };
};

export const fetchKontaktdatenCounts = async (
  teamId?: string,
  signal?: AbortSignal,
): Promise<KontaktdatenCounts> => {
  const { data } = await apiFetch<KontaktdatenCounts>("kontaktdaten-cleanup/counts", {
    params: teamId ? { teamId } : {},
    signal,
  });
  return (
    data ?? {
      a: { open: 0, erledigt: 0 },
      b: { open: 0, erledigt: 0 },
      c: { open: 0, erledigt: 0 },
    }
  );
};

/** BO-Teams für den „Verantwortlich"-Filter. */
export const fetchKontaktdatenTeams = async (
  signal?: AbortSignal,
): Promise<Array<{ id: string; name: string | null }>> => {
  const { data } = await apiFetch<Array<{ id: string; name: string | null }>>(
    "kontaktdaten-cleanup/teams",
    { signal },
  );
  return data ?? [];
};

/** POST mit JSON-Body — bewusst rohes fetch: apiFetch setzt keinen Content-Type. */
const post = async <T>(path: string, body?: unknown): Promise<T> => {
  const res = await fetch(apiUrl(path), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body ?? {}),
  });
  if (!res.ok) {
    let msg = `Aktion fehlgeschlagen: HTTP ${res.status}`;
    try {
      const d = await res.json();
      if (d?.message)
        msg = Array.isArray(d.message) ? d.message.join(", ") : String(d.message);
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return res.json() as Promise<T>;
};

/** Liste A: Zentrale-Mail vom AP zur gewählten Firma verschieben. */
export const moveToCompany = (commId: number, companyId: number) =>
  post<{ ok: true; ergebnis: "verschoben" | "dublette-geloescht" }>(
    `kontaktdaten-cleanup/${commId}/move-to-company`,
    { companyId },
  );

/** Liste B: personenhafte Mail von der Firma zum gewählten AP verschieben. */
export const moveToPerson = (commId: number, personId: number) =>
  post<{ ok: true; ergebnis: "verschoben" | "dublette-geloescht" }>(
    `kontaktdaten-cleanup/${commId}/move-to-person`,
    { personId },
  );

/**
 * „Am AP lassen" / „An Firma lassen" / „Behalten" — nur Journal; bei Liste A
 * dokumentiert eine mitgegebene companyId die bewusste Entscheidung zusätzlich
 * strukturell (kind='geschaeftlich' + companyId auf der Zeile).
 */
export const keepRow = (commId: number, liste: CleanupListe, companyId?: number) =>
  post<{ ok: true }>(`kontaktdaten-cleanup/${commId}/keep`, {
    liste,
    ...(companyId ? { companyId } : {}),
  });

/** Einzel-Löschen (hart, mit Journal-Snapshot als Undo-Netz). */
export const deleteRow = (commId: number, liste: CleanupListe) =>
  post<{ ok: true }>(`kontaktdaten-cleanup/${commId}/delete`, { liste });

/** Sammel-Löschen (Liste C, max. 200 je Aufruf). */
export const bulkDeleteRows = (commIds: number[], liste: CleanupListe) =>
  post<{ ok: true; geloescht: number }>(`kontaktdaten-cleanup/bulk-delete`, {
    commIds,
    liste,
  });

/** Rückgängig laut letzter Journal-Aktion (reopen / zurückverschieben / Re-Insert). */
export const undoRow = (commId: number) =>
  post<{ ok: true; ergebnis: string }>(`kontaktdaten-cleanup/${commId}/undo`);

// ── Mailscan-Import (Liste D) ────────────────────────────────────────────────

export interface ImportMatch {
  duplikat?: { contactId: number; name: string | null; commId: number };
  person?: { id: number; name: string | null; via: "quelle" | "name" };
  personKandidaten: Array<{ id: number; name: string | null }>;
  firmaOk: boolean;
  firmaName: string | null;
  neuAnlegen: boolean;
}

export interface ImportItemRow {
  id: number;
  batchId: number;
  typ: string;
  wert: string;
  name: string | null;
  label: string | null;
  quelleAdresse: string | null;
  domain: string | null;
  firmaId: number | null;
  status: string;
  resolvedContactId: number | null;
  resolvedAt: string | null;
  mailBetreff: string | null;
  mailDatum: string | null;
  mailWeblink: string | null;
  apKandidat: boolean;
  match: ImportMatch;
}

export interface ImportBatchRow {
  id: number;
  dateiname: string;
  zeilen: number;
  createdAt: string;
  offen: number;
  zugewiesen: number;
  verworfen: number;
}

/**
 * Quote-aware CSV-Parser für die Mailscan-Exporte (Komma-getrennt, Felder mit
 * `"` gequotet, `""` = Anführungszeichen im Feld). Liefert Objekte über die
 * Header-Zeile; unbekannte Zusatzspalten laufen einfach mit.
 */
export const parseImportCsv = (text: string): Record<string, string>[] => {
  const rows: string[][] = [];
  let cur: string[] = [];
  let field = "";
  let inQuotes = false;
  const src = text.replace(/^﻿/, "");
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      cur.push(field);
      field = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && src[i + 1] === "\n") i++;
      cur.push(field);
      field = "";
      if (cur.some((c) => c.trim() !== "")) rows.push(cur);
      cur = [];
    } else {
      field += ch;
    }
  }
  cur.push(field);
  if (cur.some((c) => c.trim() !== "")) rows.push(cur);
  if (rows.length < 2) return [];
  const header = rows[0].map((h) => h.trim());
  return rows.slice(1).map((r) => {
    const obj: Record<string, string> = {};
    header.forEach((h, idx) => {
      obj[h] = (r[idx] ?? "").trim();
    });
    return obj;
  });
};

export const previewImport = (rows: Record<string, string>[]) =>
  post<{ zeilen: number; matches: ImportMatch[]; bereitsImportiert: number }>(
    "kontaktdaten-cleanup/import/preview",
    { rows },
  );

export const commitImport = (dateiname: string, rows: Record<string, string>[]) =>
  post<{ batchId: number; angelegt: number; uebersprungen: number }>(
    "kontaktdaten-cleanup/import/commit",
    { dateiname, rows },
  );

export const fetchImportBatches = async (
  signal?: AbortSignal,
): Promise<ImportBatchRow[]> => {
  const { data } = await apiFetch<ImportBatchRow[]>(
    "kontaktdaten-cleanup/import/batches",
    { signal },
  );
  return data ?? [];
};

export const listImportItems = async (
  batchId: number,
  status: string | undefined,
  typ: string | undefined,
  q: string | undefined,
  limit: number,
  offset: number,
  signal?: AbortSignal,
): Promise<{ rows: ImportItemRow[]; total: number }> => {
  const { data, total } = await apiFetch<ImportItemRow[]>(
    "kontaktdaten-cleanup/import/items",
    {
      params: {
        batchId,
        ...(status ? { status } : {}),
        ...(typ ? { typ } : {}),
        ...(q ? { q } : {}),
        limit,
        offset,
      },
      signal,
    },
  );
  return { rows: data ?? [], total: total ?? data?.length ?? 0 };
};

/** Fund an Person/Firma zuweisen (Telefon: commType korrigierbar). */
export const assignImportItem = (
  id: number,
  body: { zielArt: "person" | "firma"; contactId: number; commType?: string; label?: string | null },
) =>
  post<{ ok: true; ergebnis: string; commId: number }>(
    `kontaktdaten-cleanup/import/items/${id}/assign`,
    body,
  );

export const verwerfenImportItem = (id: number) =>
  post<{ ok: true }>(`kontaktdaten-cleanup/import/items/${id}/verwerfen`);

export const reopenImportItem = (id: number) =>
  post<{ ok: true }>(`kontaktdaten-cleanup/import/items/${id}/reopen`);
